import { isOpenInApp, stringToBoolean } from "@repo/shared-utils/pwa-utils";
import { getSessionData } from "@repo/shared-utils/common-utils";
import { encryptBfslResumePayload } from "@repo/shared-utils/crypto";
import {
  BFSL_PWA_BASE_URL,
  DEFAULT_UTM_SOURCE_APP,
  DEFAULT_UTM_SOURCE_WEB,
  DEMAT_MODAL_KEY,
} from "./DematConstant";

export type UtmSourceParams = {
  /** AEM-authored utm_source for app. */
  utmSourceApp?: string;
  /** AEM-authored utm_source for web. */
  utmSourceWeb?: string;
};

export const isValidIndianMobileNumber = (mobileNumber: string): boolean => {
  return /^[6-9]\d{9}$/.test(mobileNumber);
};

export const hasAllRepeatingDigits = (mobileNumber: string): boolean => {
  return /^(\d)\1{9}$/.test(mobileNumber);
};
export const validateMobileNumber = (
  mobileNumber: string,
): { isValid: boolean; errorMessage: string } => {
  if (!mobileNumber) {
    return { isValid: false, errorMessage: DEMAT_MODAL_KEY.enterMobileNumber };
  } else if (mobileNumber.length !== 10) {
    return {
      isValid: false,
      errorMessage: DEMAT_MODAL_KEY.invalidMobileNumber,
    };
  } else if (
    !isValidIndianMobileNumber(mobileNumber) ||
    hasAllRepeatingDigits(mobileNumber)
  ) {
    return {
      isValid: false,
      errorMessage: DEMAT_MODAL_KEY.invalidMobileNumberFormat,
    };
  }

  return { isValid: true, errorMessage: "" };
};

export type BfslOnboardingPayload = {
  firstName: string;
  lastName: string;
  dob: string;
  mobileNo: string;
  emailId: string;
  panNo: string;
};

/**
 * Builds BFSL PWA URL with AES-CBC-256 encrypted `req` payload.
 * mobileNo is mandatory; other fields are sent as empty string when unavailable.
 * `authoredBaseUrl` comes from AEM when present; otherwise default base is used.
 */
export const buildBfslEncryptedRedirectUrl = async (
  mobileOverride?: string,
  authoredBaseUrl?: string,
): Promise<string> => {
  const session = await getSessionData();
  const mobileNo = mobileOverride ?? session?.mobileNo ?? "";

  if (!mobileNo) {
    throw new Error("mobileNo is mandatory for BFSL encrypted redirect");
  }

  const payload: BfslOnboardingPayload = {
    firstName: session?.firstName ?? "",
    lastName: session?.lastName ?? "",
    dob: session?.dob ?? "",
    mobileNo,
    emailId: session?.email ?? "",
    panNo: "",
  };

  const encryptedReq = await encryptBfslResumePayload(JSON.stringify(payload));
  const url = new URL(authoredBaseUrl ?? BFSL_PWA_BASE_URL);
  url.searchParams.set("req", encryptedReq);
  return url.toString();
};

export const redirectWithUtmParams = (
  url: string,
  redirectType: string,
  utmSources?: UtmSourceParams,
) => {
  try {
    const redirectUrl = new URL(url);
    const source = isOpenInApp()
      ? (utmSources?.utmSourceApp ?? DEFAULT_UTM_SOURCE_APP)
      : (utmSources?.utmSourceWeb ?? DEFAULT_UTM_SOURCE_WEB);
    redirectUrl.searchParams.append("utm_source", source);
    redirectUrl.searchParams.append("utm_medium", "widget_component");
    redirectUrl.searchParams.append(
      "utm_campaign",
      window.location?.href.split("/").pop() ?? "",
    );

    if (isOpenInApp()) {
      window.prompt(
        `openInAppWebViewActivity://{"url":"${redirectUrl.toString()}","title":"Bajaj Broking"}`,
      );
    } else {
      window.open(
        redirectUrl.toString(),
        stringToBoolean(redirectType) ? "_blank" : "_self",
      );
    }
  } catch (err) {
    console.error("Error constructing redirect URL:", err);

    if (isOpenInApp()) {
      window.prompt(
        `openInAppWebViewActivity://{"url":"${url}","title":"Bajaj Broking"}`,
      );
    } else {
      window.open(url, stringToBoolean(redirectType) ? "_blank" : "_self");
    }
  }
};

/**
 * Redirects to BFSL PWA with encrypted payload for resume journey.
 * Skips OTP and directly opens BFSL with encrypted user data.
 * @param mobileOverride - Optional mobile number override; uses session mobile if not provided
 * @param authoredBaseUrl - Optional custom BFSL URL from AEM
 */
export const redirectToBfslWithEncryptedPayload = async (
  mobileOverride?: string,
  authoredBaseUrl?: string,
): Promise<void> => {
  try {
    const encryptedUrl = await buildBfslEncryptedRedirectUrl(
      mobileOverride,
      authoredBaseUrl,
    );
    redirectWithUtmParams(encryptedUrl, "false");
  } catch (error) {
    console.error("Error redirecting to BFSL with encrypted payload:", error);
  }
};

export interface ISSORedirectParams {
  buySellRedirectUrl: string;
  tokenExchId: {
    exchId: string;
    formattedSymbol: string;
  };
  sectionTitle?: string;
  headerName: string;
}
